import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Upload,
  LayoutGrid,
  CreditCard,
  Wallet,
  X,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/app/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/app/components/ui/dialog";
import { NAV_ITEMS } from "@/app/config/routes";
import { cn } from "@/app/utils/tw-merge";

const ICONS: Record<string, LucideIcon> = {
  upload: Upload,
  apps: LayoutGrid,
  confirm: CreditCard,
  plans: Wallet,
};

/** Drawer kiểu Core/Drive cho mobile — dùng Dialog primitive (portal +
 *  overlay). Khác với Phase 1's MobileTopbar (list bung xuống) — drawer
 *  trượt từ trái vào, nền mờ, nút X đóng. */
export function MobileSidebar({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const onPick = (to: string) => {
    navigate(to);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="left-0 top-0 translate-x-0 translate-y-0 max-w-[280px] w-[80vw] h-screen max-h-screen rounded-none p-0 flex flex-col data-[state=open]:slide-in-from-left-2 data-[state=closed]:slide-out-to-left-2"
      >
        <div className="flex items-center justify-between border-b border-border px-4 h-14">
          <DialogTitle className="text-base text-lumina-gradient font-bold">
            Lumina Marketplace
          </DialogTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <nav className="flex-1 overflow-y-auto p-2">
          <ul className="space-y-0.5">
            {NAV_ITEMS.map(({ to, i18nKey, match }) => {
              const Icon = ICONS[i18nKey.replace("nav.", "")] ?? Upload;
              const active = match(location.pathname);
              return (
                <li key={to}>
                  <button
                    type="button"
                    onClick={() => onPick(to)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
                      active
                        ? "bg-gradient-to-r from-primary to-primary/55 text-white shadow-md"
                        : "text-muted-foreground hover:bg-primary/10 hover:text-primary"
                    )}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    <span className="truncate">{t(i18nKey)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </DialogContent>
    </Dialog>
  );
}
